import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Atom, Award, Boxes, BookOpenText, Brain, Briefcase, Calculator, ChevronDown, ClipboardList,
  Code, FileUser, FolderOpen, Gavel, GraduationCap, HeartPulse, Landmark, Layers,
  ListChecks, Mail, NotebookPen, PenLine, Puzzle, Scale, Shapes, Sigma, Smile,
  Stethoscope, Timer, TrendingUp, Users, Video, Wrench,
} from 'lucide-react';
import SeoMeta from '../../../components/SeoMeta/SeoMeta';
import { appsAPI } from '../api/appsAPI';
import { useAuth } from '../../auth/hooks/useAuth';
import { panelsFor } from '../utils/ownedApps';
import { navigateAppLaunch } from '../utils/appDisplayUtils';
import './PanelsPage.css';

/**
 * The /apps Panels page (plans-and-packages F2, DECISIONS.md section 3).
 *
 * One horizontal strip; the open panel grows. It shows what the account owns
 * and nothing else: no locked tiles, no upgrade buttons, no host or subject
 * filtering. The only input is `access.owned`, from the sign-in payload and
 * then GET /api/apps/owned. An unreadable account is an error state with a
 * retry, never an empty page (C10).
 */

const ICONS = {
  ucat: Stethoscope,
  lnat: Scale,
  tmua: Sigma,
  esat: Atom,
  tara: Brain,
  custom_quiz_generator: ListChecks,
  flash_cards: Layers,
  notes: NotebookPen,
  verbal_reasoning: BookOpenText,
  numerical_reasoning: Calculator,
  logical_reasoning: Puzzle,
  pattern_recognition: Shapes,
  personal_statement: PenLine,
  cover_letter: Mail,
  cv: FileUser,
  interview_simulator: Video,
  live_interview_medicine: HeartPulse,
  live_interview_dentistry: Smile,
  live_interview_oxbridge: Landmark,
  oxbridge_interview: GraduationCap,
  scholarship_interview: Award,
  medicine_panel_interview: Users,
  medicine_mmi_interview: Timer,
  law_interview: Gavel,
  investment_banking_interview: TrendingUp,
  operations_fs_interview: Boxes,
  private_equity_interview: Briefcase,
  computer_science_interview: Code,
  engineering_interview: Wrench,
};

const SUBLINES = {
  ucat: 'Mocks and timed subtests',
  lnat: 'Section A and the essay',
  tmua: 'Papers 1 and 2',
  esat: 'Every module, timed',
  tara: 'Thinking and reasoning',
  custom_quiz_generator: 'Questions from your own spec',
  flash_cards: 'Cards from your own material',
  notes: 'Your notes in one place',
  verbal_reasoning: 'Short timed sets',
  numerical_reasoning: 'Short timed sets',
  logical_reasoning: 'Short timed sets',
  pattern_recognition: 'Short timed sets',
  personal_statement: 'Coaching and feedback',
  cover_letter: 'Tailored to the role',
  cv: 'Written and scored',
  live_interview_medicine: 'Real mock interviews',
  live_interview_dentistry: 'Real mock interviews',
  live_interview_oxbridge: 'Real mock interviews',
  medicine_mmi_interview: 'Live stations',
};

const PANEL_LINES = {
  exam_practice: 'Papers in the real format, plus quizzes made from your own material.',
  reasoning: 'Short timed drills for the thinking skills the exams test.',
  applications: 'Your personal statement, CV and cover letter, with feedback as you write.',
  interviews: 'Mock interviews with feedback on every answer.',
};

function AppTile({ app, index, tabIndex, onOpen }) {
  const Icon = ICONS[app.key] || FolderOpen;
  const sub = SUBLINES[app.key] || 'Live practice';
  return (
    <button
      type="button"
      className="panels-app"
      style={{ '--i': index }}
      tabIndex={tabIndex}
      onClick={() => onOpen(app)}
    >
      <span className="panels-mono"><Icon size={22} strokeWidth={1.75} aria-hidden="true" /></span>
      <span className="panels-app-text">
        <span className="panels-app-title">{app.title}</span>
        <span className="panels-app-sub">{sub}</span>
      </span>
    </button>
  );
}

/**
 * A panel's tiles. A grouped panel (Interviews) shows each group under a
 * header button that expands or collapses it, so an account with many
 * packages and Oxbridge subjects (org_full) does not scroll through all of
 * them. The first group starts open and the rest closed; a collapsed group's
 * tiles stay in the document, hidden, so the panel's count still matches.
 */
function PanelBody({ panel, tabIndex, onOpen }) {
  const [toggled, setToggled] = useState({});
  if (!panel.groups) {
    return (
      <div className="panels-group">
        <div className="panels-list">
          {panel.apps.map((app, i) => (
            <AppTile key={app.key} app={app} index={i} tabIndex={tabIndex} onOpen={onOpen} />
          ))}
        </div>
      </div>
    );
  }
  let n = 0;
  return panel.groups.map((group, groupIndex) => {
    const expanded = toggled[group.id] ?? groupIndex === 0;
    const listId = `panels-group-${panel.id}-${group.id}`;
    return (
      <div className={`panels-group${expanded ? ' panels-group--open' : ''}`} key={group.id}>
        <h3 className="panels-group-title">
          <button
            type="button"
            className="panels-group-toggle"
            aria-expanded={expanded}
            aria-controls={listId}
            tabIndex={tabIndex}
            onClick={() => setToggled((prev) => ({ ...prev, [group.id]: !expanded }))}
          >
            <span className="panels-group-name">{group.title}</span>
            <span className="panels-group-count">{group.apps.length}</span>
            <ChevronDown className="panels-group-chevron" size={18} strokeWidth={2} aria-hidden="true" />
          </button>
        </h3>
        <div className="panels-list" id={listId} hidden={!expanded}>
          {group.apps.map((app) => (
            <AppTile
              key={app.key}
              app={app}
              index={expanded ? n++ : 0}
              tabIndex={expanded ? tabIndex : -1}
              onOpen={onOpen}
            />
          ))}
        </div>
      </div>
    );
  });
}

const countOf = (panel) => (panel.groups || [panel]).reduce((sum, g) => sum + g.apps.length, 0);

export default function UserPurchasedAppsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const authOwned = user?.access?.owned ?? null;

  // The latest successful read for this user. Null until one lands.
  const [fetched, setFetched] = useState({ userId: null, owned: null });
  const [failed, setFailed] = useState(false);
  const [openId, setOpenId] = useState(null);
  const requestRef = useRef(0);

  const owned = fetched.userId === userId && fetched.owned ? fetched.owned : authOwned;

  const load = useCallback(async () => {
    if (!userId) return;
    const request = ++requestRef.current;
    try {
      const body = await appsAPI.getOwned();
      if (request !== requestRef.current) return;
      if (!body || typeof body !== 'object' || Array.isArray(body)) throw new Error('owned_malformed');
      setFetched({ userId, owned: body });
      setFailed(false);
    } catch (err) {
      if (request !== requestRef.current) return;
      setFailed(true);
    }
  }, [userId]);

  // The pane stays mounted across tabs (ChatShell), so a purchase made on the
  // pricing page is picked up when /apps becomes active again and on focus.
  // The router's own path (basename-free), read by the focus handler below;
  // window.location.pathname is '/edu/apps' under the /edu basename.
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  useEffect(() => {
    if (location.pathname === '/apps') load();
  }, [location.pathname, load]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== 'hidden' && pathRef.current === '/apps') load();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      requestRef.current += 1;
    };
  }, [load]);

  const result = useMemo(() => panelsFor(owned), [owned]);
  const panels = result.panels;
  const activeId = panels.some((p) => p.id === openId) ? openId : panels[0]?.id;

  const openApp = (app) => navigateAppLaunch(navigate, app.launch);

  // Show the error only when there is nothing readable to show: a refresh that
  // fails after a good read keeps the last good view.
  const showError = result.status === 'error' && failed;
  const showLoading = result.status === 'error' && !failed;

  return (
    <div className="panels-page">
      <SeoMeta
        title="Apps — Ivori"
        description="Open the apps and tools your account includes."
      />
      <div className="panels-inner">
        <h1 className="panels-title">Your apps</h1>

        {showLoading && <div className="panels-state" role="status"><p>Loading your apps…</p></div>}

        {showError && (
          <div className="panels-state" role="alert">
            <p>We could not load your apps just now. Please try again.</p>
            <button type="button" className="panels-retry" onClick={load}>Try again</button>
          </div>
        )}

        {result.status === 'ok' && (
          <div className="panels-strip">
            {panels.map((panel) => {
              const isOpen = panel.id === activeId;
              return (
                <section
                  key={panel.id}
                  className={`panels-pan${isOpen ? ' panels-pan--open' : ''}`}
                  aria-label={panel.title}
                >
                  <button
                    type="button"
                    className="panels-hit"
                    aria-expanded={isOpen}
                    aria-label={`Open ${panel.title}`}
                    onClick={() => setOpenId(panel.id)}
                  />
                  <span className="panels-count" aria-hidden="true">{countOf(panel)}</span>
                  <span className="panels-vlabel" aria-hidden="true">{panel.title}</span>
                  <div className="panels-content">
                    <h2 className="panels-heading">{panel.title}</h2>
                    <p className="panels-line">{PANEL_LINES[panel.id]}</p>
                    <PanelBody panel={panel} tabIndex={isOpen ? 0 : -1} onOpen={openApp} />
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
